export interface ResizeCallbackPayload {
  innerWidth: number;
  innerHeight: number;
}

export interface ResizeCallback {
  (payload: ResizeCallbackPayload): void;
}

export interface ProxyTarget {
  el?: Element | Window;
  getWidth: () => number;
  getHeight: () => number;
}
/**
 * `el` is a root element from which to measure the width/height.
 *  Usually you wouldn't set `el` but its useful in Storybook.
 * @param el
 * @returns {{onResize: onResize, destroyHandler: destroyHandler}}
 */
function createResizeHandler(el?: Element) {
  let func: () => void;
  let observer: ResizeObserver | MutationObserver;

  function getProxyTarget(): ProxyTarget {
    return {
      el: el ?? window,
      getWidth: el ? () => el.clientWidth : () => window.innerWidth,
      getHeight: el ? () => el.clientHeight : () => window.innerHeight,
    };
  }

  const context = new Proxy(getProxyTarget(), {});
  const onResize = function resize(callback: ResizeCallback) {
    func = () => {
      callback({
        innerWidth: context.getWidth(),
        innerHeight: context.getHeight(),
      });
    };

    if (!el) {
      window.addEventListener("resize", func);
    } else {
      observer = new ResizeObserver(func);
      observer.observe(el);
    }
    func();
  };
  const destroy = () => {
    if (!el) {
      window.removeEventListener("resize", func);
    } else {
      observer.disconnect();
    }
  };
  return { onResize, destroy };
}

export { createResizeHandler };
